import axios from 'axios';
import { unionBy } from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { orderProducts } from '../functions/common';
import { State, WrappedUseState, WrappedUseStateReturn } from '../functions/hooks';
import IsMountedWrapper from '../functions/isMountedWrapper';
import { convertRezdyProduct } from '../functions/rezdy';
import { GSLEntities } from '../models/bookings';
import { Product, emptyPackageProduct, emptyProduct } from '../models/products';
import { RezdyPriceOption } from '../models/rezdyObjects';
import { BaseStore, BaseStoreValues, WrappedSetter } from './BaseStore';
import { DefaultProviderProps, createStore, createStoreContext } from './common';
import { baseProducts } from './productCodes';

const apiURL = import.meta.env.PROD ? '/api/' : 'http://localhost:5000/api/';

export interface SessionValue {
    date: string;
    startTime: string;
    seatsRemaining: number;
    id: string;
    priceOptions?: RezdyPriceOption[];
}

export interface AvailabilityValue {
    departureDate: string;
    packageDepartureDate?: string;
    sessions?: SessionValue[];
    packageSessions?: SessionValue[];
}

export interface ProductsStoreValue extends BaseStoreValues<Product> {
    readonly products: Product[];
    readonly product: Product;
    readonly packageProducts: Product[];
    readonly packageProduct: WrappedUseStateReturn<Product>;
    readonly setProduct: WrappedSetter<Product>;
    readonly setProducts: WrappedSetter<Product[]>;
    readonly getAvailability: () => void;
    /**
     * Eagerly fetch availability for the upcoming `days` (default 7) so date selection is instant.
     * De-duped per product/day via refs, so repeat/overlapping calls are cheap no-ops.
     */
    readonly preloadAvailability: (days?: number) => void;
    readonly availability: AvailabilityValue;
    /**
     * Loading flag scoped to availability requests only. Kept SEPARATE from `isLoading`
     * (which tracks the product catalogue) so fetching availability never flips the
     * catalogue skeletons / re-downloads product imagery.
     */
    readonly availabilityLoading: boolean;
    readonly setAvailability: WrappedSetter<AvailabilityValue>;
    readonly clearController: () => void;
    readonly loadProductInfo: (prods: Product[]) => void;
}

export const ProductsStore = createStoreContext<ProductsStoreValue>();

interface ProductsStoreProviderProps extends DefaultProviderProps {
    /**
     * Products fetched server-side at build (prerender). When supplied, the catalogue
     * is seeded synchronously so the server-rendered HTML contains real product content
     * and the client hydrates from the same data (no mismatch); the mount effect still
     * refreshes live prices/availability afterwards.
     */
    readonly initialProducts?: Product[];
}

export const ProductsStoreProvider = ({ children, initialProducts }: ProductsStoreProviderProps) => {
    const hasInitial = Boolean(initialProducts?.length);
    const base = BaseStore<Product>(emptyProduct, hasInitial ? (initialProducts as Product[]) : [], !hasInitial);
    const isMounted = IsMountedWrapper();
    const availability = State<AvailabilityValue>({ departureDate: moment().toISOString() }, isMounted);
    const availabilityLoading = WrappedUseState<boolean>(false);
    const packageProducts = WrappedUseState<Product[]>([emptyPackageProduct]);
    const packageProduct = WrappedUseState<Product>(emptyPackageProduct);

    // Synchronous mirrors of the session caches. State updates are async, so when we
    // preload several days back-to-back the closures still see stale `availability.data`;
    // these refs let each fetch know what's already cached and skip redundant calls.
    const sessionsRef = useRef<SessionValue[]>([]);
    const packageSessionsRef = useRef<SessionValue[]>([]);
    // Day-level in-flight guards (keyed by product + day) so overlapping fetches for the
    // same day can't fire twice (e.g. CalendarDateSel mount + product preload colliding).
    const inFlightRef = useRef<Set<string>>(new Set());
    const inFlightPackageRef = useRef<Set<string>>(new Set());
    // Reference-counted busy flag: stays "loading" for the whole preload window, not just one day.
    const pendingRef = useRef(0);

    const setAvailabilityBusy = (delta: number) => {
        pendingRef.current = Math.max(0, pendingRef.current + delta);
        availabilityLoading.setData(pendingRef.current > 0, isMounted);
    };

    const clearController = () => {
        base.setMainObject(emptyProduct, isMounted);
        packageProduct.setData(emptyPackageProduct, isMounted);
        availability.set({ departureDate: moment().toISOString() });
        sessionsRef.current = [];
        packageSessionsRef.current = [];
    };

    // The Rezdy proxy intermittently returns a wrapped 401/error (inside an HTTP 200)
    // while its upstream token is refreshing. Retry across that window with backoff so a
    // single bad moment at page load doesn't leave the whole session without products.
    const RETRY_DELAYS_MS = [800, 2000, 4000];

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const fetchRezdyProduct = async (url: string, build: (raw: any) => Product) => {
        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            try {
                const result = await axios(url);
                if (result.data?.requestStatus?.success) {
                    return build(result.data.product);
                }
            } catch (e) {
                if (attempt === RETRY_DELAYS_MS.length) {
                    console.error('Failed to load product', url, e);
                }
            }
            if (attempt < RETRY_DELAYS_MS.length) {
                await sleep(RETRY_DELAYS_MS[attempt]);
            }
        }
        return undefined;
    };

    const callProduct = (product: Product) =>
        fetchRezdyProduct(
            `${apiURL}rezdy/products?productCode=${product.productCode}&entity=${product.entity}`,
            (raw) => convertRezdyProduct(product, raw)
        );

    const callPackageProduct = (product: Product) =>
        fetchRezdyProduct(
            `${apiURL}rezdy/products?productCode=${product.packageCode}&entity=${product.packageEntity}`,
            (raw) => convertRezdyProduct({ ...emptyPackageProduct, entity: product.packageEntity }, raw)
        );

    const loadProductInfo = async (prods: Product[]) => {
        base.setIsLoading(true, isMounted);
        const completedProds = prods.map((product) => callProduct(product));
        const packageProds = prods.map((product) =>
            product.allowPackage ? callPackageProduct(product) : Promise.resolve(undefined)
        );
        Promise.allSettled(completedProds)
            .then((results) => {
                const fetched = results
                    .map((res) => (res.status === 'fulfilled' ? res.value : undefined))
                    .filter((p): p is Product => Boolean(p));
                const sortedArray = orderProducts(fetched).filter((p): p is Product => Boolean(p));
                // Never overwrite a populated catalogue with an empty/failed result.
                if (sortedArray.length) {
                    base.setMainEntities(sortedArray, isMounted);
                }
            })
            .finally(() => {
                base.setIsLoading(false, isMounted);
            });
        Promise.allSettled(packageProds).then((results) => {
            const packageArray = results
                .map((res) => (res.status === 'fulfilled' ? res.value : undefined))
                .filter((x): x is Product => Boolean(x));
            if (packageArray.length) {
                packageProducts.setData(packageArray, isMounted);
            }
        });
    };

    const dayIsCached = (sessions: SessionValue[], day?: string) =>
        Boolean(day && sessions.find((s) => moment(s.startTime).isSame(day, 'day')));

    // Fetch a single day's availability for the current product. Skips the network when the
    // day is already cached (covers the case where one response returns a multi-day window),
    // and never touches the catalogue `isLoading`.
    const fetchAvailabilityForDate = async (rawDate?: string) => {
        const product = base.mainObject;
        if (!product?.productCode || !rawDate) return;
        if (dayIsCached(sessionsRef.current, rawDate)) {
            product.allowPackage && getPackageAvailability();
            return;
        }
        const cacheKey = `${product.productCode}|${moment(rawDate).format('YYYY-MM-DD')}`;
        if (inFlightRef.current.has(cacheKey)) return;
        inFlightRef.current.add(cacheKey);
        setAvailabilityBusy(1);
        try {
            const date = moment(rawDate).toISOString();
            const response = await axios(
                `${apiURL}rezdy/availability?date=${date}&product=${product.productCode}&entity=${product.entity}`
            );
            const fetched = (response.data as SessionValue[]) || [];
            sessionsRef.current = unionBy(sessionsRef.current, fetched, 'id');
            availability.set((prev) => ({
                ...prev,
                sessions: prev.sessions ? unionBy(prev.sessions, fetched, 'id') : fetched,
            }));
            product.allowPackage && getPackageAvailability();
        } catch (e) {
            console.error(e);
        } finally {
            inFlightRef.current.delete(cacheKey);
            setAvailabilityBusy(-1);
        }
    };

    // Back-compat entry point used by CalendarDateSel: fetch the currently selected departure date.
    const getAvailability = () => fetchAvailabilityForDate(availability.data.departureDate);

    const preloadAvailability = async (days = 7) => {
        if (!base.mainObject?.productCode) return;
        // Hold the busy flag across the whole window so the time picker doesn't flicker per day.
        setAvailabilityBusy(1);
        try {
            for (let i = 0; i < days; i++) {
                await fetchAvailabilityForDate(moment().add(i, 'days').toISOString());
            }
        } finally {
            setAvailabilityBusy(-1);
        }
    };

    const getPackageAvailability = async () => {
        const product = base.mainObject;
        if (!product?.packageCode) return;
        const rawDate = availability.data.packageDepartureDate || availability.data.departureDate;
        if (dayIsCached(packageSessionsRef.current, rawDate)) return;
        const cacheKey = `${product.packageCode}|${moment(rawDate).format('YYYY-MM-DD')}`;
        if (inFlightPackageRef.current.has(cacheKey)) return;
        inFlightPackageRef.current.add(cacheKey);
        setAvailabilityBusy(1);
        try {
            const date = moment(rawDate).toISOString();
            const response = await axios(
                `${apiURL}rezdy/availability?date=${date}&product=${product.packageCode}&entity=${product.packageEntity}`
            );
            const fetched = (response.data as SessionValue[]) || [];
            packageSessionsRef.current = unionBy(packageSessionsRef.current, fetched, 'id');
            availability.set((prev) => ({
                ...prev,
                packageSessions: prev.packageSessions ? unionBy(prev.packageSessions, fetched, 'id') : fetched,
            }));
        } catch (e) {
            console.error(e);
        } finally {
            inFlightPackageRef.current.delete(cacheKey);
            setAvailabilityBusy(-1);
        }
    };

    const getPickups = async (pickupId: number, entity: GSLEntities) => {
        try {
            const response = await axios(`${apiURL}rezdy/pickups?pickupId=${pickupId}&entity=${entity}`);
            response &&
                base.setMainObject(
                    (prev) => ({
                        ...prev,
                        pickupList: response.data,
                    }),
                    isMounted
                );
        } catch (e) {
            console.error(e);
        }
    };

    const getPackagePickups = async (pickupId: number, entity: GSLEntities) => {
        try {
            const response = await axios(`${apiURL}rezdy/pickups?pickupId=${pickupId}&entity=${entity}`);
            response &&
                packageProduct.setData(
                    (prev) => ({
                        ...prev,
                        pickupList: response.data,
                    }),
                    isMounted
                );
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        base.mainObject.allowPackage &&
            packageProduct.data?.pickupID &&
            getPackagePickups(packageProduct.data.pickupID, packageProduct.data.entity);
    }, [packageProduct.data?.pickupID]);

    useEffect(() => {
        base.mainObject?.pickupID && getPickups(base.mainObject.pickupID, base.mainObject.entity);
    }, [base.mainObject?.pickupID]);

    useEffect(() => {
        if (baseProducts) {
            loadProductInfo(baseProducts);
        }
    }, []);

    // Keep the synchronous mirrors aligned whenever the cached sessions change for any reason.
    useEffect(() => {
        sessionsRef.current = availability.data.sessions || [];
    }, [availability.data.sessions]);

    useEffect(() => {
        packageSessionsRef.current = availability.data.packageSessions || [];
    }, [availability.data.packageSessions]);

    // When the selected product changes, drop the previous tour's sessions (so they can't
    // bleed across) and eagerly preload the next 7 days for the new product.
    useEffect(() => {
        sessionsRef.current = [];
        packageSessionsRef.current = [];
        availability.set((prev) => ({ departureDate: prev.departureDate }));
        if (base.mainObject?.productCode) {
            preloadAvailability(7);
        }
    }, [base.mainObject?.productCode]);

    useEffect(() => {
        base.mainObject.allowPackage && availability.data.packageDepartureDate && getPackageAvailability();
    }, [availability.data.packageDepartureDate]);

    return createStore(ProductsStore, children, {
        ...base,
        product: base.mainObject,
        setProduct: base.setMainObject,
        products: base.mainEntities,
        setProducts: base.setMainEntities,
        packageProducts: packageProducts.data,
        packageProduct: packageProduct,
        availability: availability.data,
        availabilityLoading: availabilityLoading.data,
        setAvailability: availability.set,
        getAvailability,
        preloadAvailability,
        clearController,
        loadProductInfo,
    });
};
